import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

// The transition list names exactly what the variants change: compositor-cheap
// properties only, per specs/features/ui-design-system.feature.
//
// The PRESS state uses the Kbd chip's physics — sink a pixel, shrink a hair — and
// runs faster than the hover (80ms vs 130ms) so it reads as a separate, sharper
// beat.
//
// Icon motion rides on `data-slot="button"` (see index.css) rather than a `group`
// class, so any icon inside any button can opt in without the call site marking
// the button as a group.
//
// FOCUS is the app-wide soft halo (`.glow-on-focus`), never Tailwind's `ring-*` —
// that compiles to a spread with no blur, so it can only ever draw a hard edge.
// `ring-0` cancels the generated ring; the destructive variant recolours the halo
// by overriding `--ring-glow`.
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap outline-none transition-[color,background-color,border-color,box-shadow,transform,opacity,filter] duration-[var(--motion-interaction)] ease-out glow-on-focus focus-visible:ring-0 active:translate-y-px active:scale-[0.98] active:duration-[var(--motion-press)] disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 motion-reduce:transition-none motion-reduce:active:translate-y-0 motion-reduce:active:scale-100 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      // TWO HOVER MECHANISMS, chosen by whether the surface already has colour.
      //
      // NEUTRAL surfaces (outline, ghost, secondary) swap to a brighter TOKEN —
      // exact, themeable, and it touches only the background.
      //
      // FILLED surfaces (default, destructive) raise BRIGHTNESS, which preserves
      // hue and lifts on either ground. Brightness suits filled surfaces only: the
      // filter applies to the whole subtree, so on a ghost button it would brighten
      // the label rather than a background that is not there.
      //
      // Light takes 1.10; dark takes the LARGER 1.25 — `--primary` there is a deep
      // teal (L 0.437), where a 5% lift never registers. Direction still follows
      // the ground (docs/ui-effects.md §1.2b): both lift toward the light.
      variant: {
        default:
          "bg-primary text-primary-foreground hover:brightness-110 dark:hover:brightness-125",
        destructive:
          // Focus is the same halo, recoloured by swapping the token it reads —
          // not a second box-shadow.
          "bg-destructive text-white hover:brightness-110 [--ring-glow:var(--destructive-glow)] dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
